import { safeGet } from "./safeStorage";

/**
 * All-caps and font scale, applied to the document root.
 *
 * WHY THIS IS NOT A REACT EFFECT ANY MORE (v17.06)
 *
 * GlobalModeWrapper held `allCaps` in state and wrote the class from an effect
 * with deps [allCaps, fontPx]. The storage listener did:
 *
 *     setAllCaps(safeGet('allCaps') === 'true');
 *
 * When the stored value has not CHANGED, that setState is a no-op: React bails
 * out, there is no re-render, the effect never runs, and the class is never
 * written. So the document's appearance was only ever corrected at the moment
 * the value flipped.
 *
 * That is fine while nothing else touches the class - and fatal the moment
 * anything does, because there is then no path back. Ivan hit exactly this:
 * the All Caps toggle read ON, the whole app (including the bottom nav)
 * rendered in normal case, and it stayed that way through font-size changes
 * and navigation. Toggling All Caps off and on fixed it, because only a real
 * change re-ran the effect.
 *
 * The rule now: every event re-asserts the truth from storage onto the DOM,
 * unconditionally. Re-applying a class that is already correct costs nothing;
 * being unable to re-apply one costs the user a broken-looking app with no way
 * back except finding the toggle.
 */

export const DEFAULT_FONT_PX = 20;

/**
 * Read the display preferences. Never throws, always returns usable values -
 * a corrupt fontPx must not leave --fs as NaN, which collapses every scaled
 * font in the app.
 */
export function readDisplayMode() {
  const allCaps = safeGet("allCaps") === "true";
  const raw = parseInt(safeGet("fontPx") || String(DEFAULT_FONT_PX), 10);
  const fontPx = Number.isFinite(raw) && raw > 0 ? raw : DEFAULT_FONT_PX;
  return { allCaps, fontPx };
}

/**
 * Write those preferences onto a document root. Idempotent by design.
 * @param {Element} root - document.documentElement (or a stand-in, in tests)
 * @param {{allCaps: boolean, fontPx: number}} mode
 */
export function applyDisplayMode(root, mode) {
  if (!root || !root.classList) return;
  const { allCaps, fontPx } = mode || {};
  if (allCaps) {
    root.classList.add("all-caps");
  } else {
    root.classList.remove("all-caps");
  }
  const px = Number.isFinite(fontPx) && fontPx > 0 ? fontPx : DEFAULT_FONT_PX;
  if (root.style && typeof root.style.setProperty === "function") {
    root.style.setProperty("--fs", String(px / DEFAULT_FONT_PX));
  }
}

/** Read storage and apply it in one go - what every listener calls. */
export function syncDisplayMode(root) {
  applyDisplayMode(root, readDisplayMode());
}

export default syncDisplayMode;
